import React from 'react';
import { Button, Flex, Select, TagBadge, Text, useToast } from '@chatsdet/ui';
import { FilingTaxonomyFields } from '../../../features/tickets/components/FilingTaxonomyFields';
import {
  useFilingSettings,
  useLinkTicket,
  useTicketDuplicates,
  useTicketTaxonomy,
} from '../../../hooks/useTicketingProviders';
import { toastErrorOptionsFor } from '../../../lib/toast-error-options';

type FilingEdits = { repo?: string | null; type?: string | null; labels?: string[] };

/**
 * Per-ticket filing options for providers with filing settings: type and labels (and, for
 * GitHub, the repo) start from the org defaults and can be changed for this ticket only, plus
 * possible duplicates. Until the settings load there are no options, and filing sends none so
 * the server applies the org defaults itself.
 */
export function useFilingOptions({
  providerId,
  enabled,
  isConfiguring,
  issueId,
  onLinked,
}: {
  providerId: string;
  enabled: boolean;
  isConfiguring: boolean;
  issueId: string;
  onLinked: () => void;
}) {
  const { toast } = useToast();
  const settingsQuery = useFilingSettings(providerId, { enabled });
  const settings = settingsQuery.data?.data;
  const github = settings?.extensions?.github;
  const trackerRepo = github?.trackerRepo ?? null;

  // What the user changed for this ticket; anything else is the org default.
  const [edits, setEdits] = React.useState<FilingEdits>({});
  React.useEffect(() => {
    if (!isConfiguring) setEdits({});
  }, [isConfiguring]);

  const repo = edits.repo !== undefined ? edits.repo : trackerRepo;
  const type = edits.type !== undefined ? edits.type : (settings?.defaultType ?? null);
  const labels = edits.labels ?? settings?.defaultLabels ?? [];

  const active = isConfiguring && settings !== undefined;
  const taxonomyQuery = useTicketTaxonomy(providerId, repo, active);
  const duplicatesQuery = useTicketDuplicates(providerId, issueId, repo, active);
  const linkTicket = useLinkTicket(providerId);
  const taxonomy = taxonomyQuery.data?.data;
  const duplicates = duplicatesQuery.data?.data ?? [];
  const repos = taxonomy?.extensions?.github.repos ?? (trackerRepo ? [trackerRepo] : []);

  const changeRepo = (next: string | null) => {
    // Types and labels belong to a repo; the saved defaults only apply to the tracker repo.
    setEdits(next === trackerRepo ? {} : { repo: next, type: null, labels: [] });
  };

  const link = (ticketUrl: string) => {
    linkTicket.mutate(
      { issueId, ticket: ticketUrl },
      {
        onSuccess: (data) => {
          toast.success(`Linked ${data.ticket.identifier}`);
          onLinked();
        },
        onError: (error) =>
          toast.error(
            error instanceof Error ? error.message : 'Failed to link ticket',
            toastErrorOptionsFor(error)
          ),
      }
    );
  };

  const optionFields = (
    <Flex direction="column" gap="md" className="w-full">
      {github && (
        <Select
          label="Repository"
          selectedKey={repo}
          items={repos.map((name) => ({ id: name, label: name }))}
          placeholder="Application's repository"
          onSelectionChange={(key) => changeRepo(key ? String(key) : null)}
        />
      )}
      <FilingTaxonomyFields
        taxonomy={taxonomyQuery.isPlaceholderData ? undefined : taxonomy}
        type={type}
        labels={labels}
        onTypeChange={(next) => setEdits((current) => ({ ...current, type: next }))}
        onLabelsChange={(next) => setEdits((current) => ({ ...current, labels: next }))}
      />
      {duplicates.length > 0 && (
        <Flex direction="column" gap="xs">
          <Text size="sm" weight="medium">
            Possible duplicates
          </Text>
          {duplicates.map((duplicate) => (
            <Flex key={duplicate.externalId} gap="sm" align="center">
              <a
                href={duplicate.url}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary hover:underline truncate"
              >
                {duplicate.identifier} {duplicate.title}
              </a>
              <TagBadge name={duplicate.status} color="gray" hideDot size="sm" />
              <Button
                variant="ghost"
                size="sm"
                onPress={() => link(duplicate.url)}
                isDisabled={linkTicket.isPending}
              >
                Link instead
              </Button>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );

  return {
    optionFields,
    providerOptions: active
      ? { ...(repo ? { repoFullName: repo } : {}), type, labels }
      : undefined,
    shouldConfirmBeforeCreate: settings !== undefined,
  };
}
